import { api } from "./api";
import { COOKIE_CONSENT_KEY } from "./cookieConsentService";
import { getReferralCode, clearReferralCode } from "./referralService";

/*
  authService — הזדהות המנוי (UI_SPEC ס' 2): הרשמה בעת הרכישה (שם משתמש +
  סיסמה) וכניסה חוזרת. שומר את ה-JWT ופרטי המנוי ב-localStorage; ‏api.js
  מצרף את ה-token לכל בקשה. תוקף המנוי (עד 30.8 של השנה שאחרי) נאכף בשרת.
*/
export const TOKEN_KEY = "vaadygo.token";
const USER_KEY = "vaadygo.user";
// מי הבעלים של הנתונים המקומיים במכשיר — לזיהוי החלפת משתמש
const DATA_OWNER_KEY = "vaadygo.dataOwner";
// סימון "משתמש חדש" (נקבע בהרשמה בלבד) — מפעיל את פופאפ הברוכים-הבאים פעם אחת
const NEW_USER_KEY = "vaadygo.isNewUser";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function getUser() {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY));
  } catch {
    return null;
  }
}

export function isAuthenticated() {
  return Boolean(getToken());
}

/*
  האם תוקף המנוי/הניסיון פג — לפי התאריך שנשמר בכניסה/הרשמה. משמש לנעילה
  בזמן אמת (הפניה למסך החידוש). אם אין תאריך — לא נועלים (בטיחות: לא חוסמים
  משתמש בגלל מידע חסר). ה-JWT ממילא פג באותו תאריך, אז השרת אוכף גם הוא.
*/
/*
  האם לחסום את המשתמשת בגלל תוקף מנוי.

  **החלטת בעלת המוצר (22.08.2026): לא חוסמים.** בתום חודש הניסיון המשתמשת
  עוברת ל**מסלול החינמי** — נכנסת כרגיל, רואה את כל מה שיצרה, ורק פיצ'רי
  הפרו ננעלים (ProGate בלקוח, 402 בשרת). חסימה הייתה מנתקת אותה מהנתונים
  שלה עצמה, וזה בדיוק ההפך מהמודל.

  הפונקציה נשמרת כנקודה אחת שאפשר להחזיר בה חסימה בעתיד (למשל אי-תשלום של
  מנוי שכבר נרכש), במקום לפזר את ההחלטה בין App.js למסך הכניסה.
*/
export function isSubscriptionExpired() {
  return false;
}

/*
  האם כבר נכנסו לאפליקציה במכשיר הזה בעבר (משתמש חוזר). הסימן: קיים "בעל
  נתונים" — מישהו כבר נרשם/התחבר כאן. משמש להחלטה בכניסה: משתמש חדש רואה קודם
  את מסך הברוכים-הבאים, ומשתמש חוזר מגיע ישר למסך הכניסה.
*/
export function hasVisitedBefore() {
  try {
    return localStorage.getItem(DATA_OWNER_KEY) !== null;
  } catch {
    return false;
  }
}

/* מסמן שהמשתמש נרשם עכשיו לראשונה — לפופאפ הברוכים-הבאים (פעם אחת) */
export function markNewUser() {
  try {
    localStorage.setItem(NEW_USER_KEY, "1");
  } catch {
    // storage חסום — הפופאפ פשוט לא יוצג; לא קריטי
  }
}

/* האם זהו משתמש חדש שטרם ראה את פופאפ הברוכים-הבאים */
export function isNewUser() {
  try {
    return localStorage.getItem(NEW_USER_KEY) === "1";
  } catch {
    return false;
  }
}

/* אחרי שפופאפ הברוכים-הבאים הוצג ונסגר — כבר לא "חדש" */
export function clearNewUser() {
  try {
    localStorage.removeItem(NEW_USER_KEY);
  } catch {
    // ignore
  }
}

/* האם המשתמשת היא מנהלת-על (לניהול ספקים — UI_SPEC ס' 12) */
export function isSuperAdmin() {
  return getUser()?.role === "SuperAdmin";
}

/*
  מעדכן מקומית את מסלול המשתמשת (free/pro) במכשיר הזה — לפתיחת פרו בצד הלקוח
  (שלב ביניים עד שהמסלול יגיע מהשרת). לא עושה כלום אם אין משתמשת מחוברת.
*/
export function setLocalPlan(plan) {
  const user = getUser();
  if (!user) {
    return;
  }
  localStorage.setItem(USER_KEY, JSON.stringify({ ...user, plan }));
}

/*
  מנקה את כל הנתונים המקומיים שקשורים למשתמש/מוסד (מוסדות, אשף, צוות, אירועים,
  מתנות, תקציבים, התראות שנקראו, הגדרות) — כל מפתחות vaadygo.* חוץ מפרטי ההזדהות.
*/
function clearCachedAppData() {
  // בחירת העוגיות היא העדפת מכשיר (לא נתוני חשבון) — שומרים אותה גם בהחלפת משתמש,
  // כדי שבאנר האישור לא יקפוץ שוב אחרי שכבר בחרו במכשיר הזה.
  // אסימון המכשיר (2FA "זכור אותי") שייך למכשיר ולא לחשבון — נשמר גם בהחלפת
  // משתמש וגם ביציאה, כדי שלא יידרש קוד מחדש בכל פעם במכשיר מוכר.
  const keep = new Set([
    TOKEN_KEY,
    USER_KEY,
    DATA_OWNER_KEY,
    COOKIE_CONSENT_KEY,
    DEVICE_TOKEN_KEY,
  ]);
  Object.keys(localStorage)
    .filter((key) => key.startsWith("vaadygo.") && !keep.has(key))
    .forEach((key) => localStorage.removeItem(key));
}

function store(auth) {
  // אבטחה: אם מתחבר משתמש *אחר* מזה שהנתונים המקומיים שייכים לו (אותו מכשיר) —
  // מנקים את כל המטמון המקומי כדי שלא יראה מוסדות/צוות של משתמש קודם.
  // כניסה חוזרת של אותו משתמש שומרת את הנתונים שלו.
  if (localStorage.getItem(DATA_OWNER_KEY) !== auth.username) {
    clearCachedAppData();
  }
  localStorage.setItem(DATA_OWNER_KEY, auth.username);
  localStorage.setItem(TOKEN_KEY, auth.token);
  localStorage.setItem(
    USER_KEY,
    JSON.stringify({
      username: auth.username,
      email: auth.email,
      role: auth.role,
      subscriptionValidUntil: auth.subscriptionValidUntil,
    })
  );
}

export async function register({ username, email, phone, password }) {
  // קוד הפניה (?ref=) שנלכד בקישור ההרשמה — נשלח כדי שהמנהלת תראה מקור ההרשמה.
  const ref = getReferralCode();
  const auth = await api.post("/api/auth/register", {
    username,
    email,
    phone,
    password,
    ...(ref ? { ref } : {}),
  });
  clearReferralCode(); // נשלח — לא צריך לדבוק להרשמה הבאה במכשיר
  // אימות מייל נדרש — עדיין אין טוקן. הכניסה תושלם אחרי הזנת הקוד (verifyTwoFactor),
  // ואז העמוד מסמן "משתמש חדש". לא שומרים כלום עד שהקוד אומת.
  if (auth?.twoFactorRequired) {
    return auth;
  }
  store(auth);
  markNewUser(); // משתמש חדש — יראה פעם אחת את פופאפ הברוכים-הבאים
  return auth;
}

/*
  אסימון "זכור את המכשיר הזה" מהאימות הדו-שלבי. שייך למכשיר ולא לחשבון, ולכן
  שורד יציאה מהמערכת — אחרת הוא היה חסר טעם. אינו מזהה בפני עצמו: השרת בודק
  אותו רק אחרי שהסיסמה אומתה, ומול המשתמש הספציפי.
*/
const DEVICE_TOKEN_KEY = "vaadygo.deviceToken";

function getDeviceToken() {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY) || undefined;
  } catch {
    return undefined;
  }
}

function storeDeviceToken(token) {
  if (!token) {
    return;
  }
  try {
    localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    // storage חסום — פשוט יידרש קוד גם בכניסה הבאה
  }
}

/*
  כניסה. שימי לב לערך המוחזר: אם למשתמש יש אימות דו-שלבי, השרת מחזיר
  `{ twoFactorRequired: true, challengeId, ... }` **בלי טוקן**, ואז אסור
  לשמור כלום — הכניסה תושלם רק ב-verifyTwoFactor.
*/
export async function login({ usernameOrEmail, password }) {
  const auth = await api.post("/api/auth/login", {
    usernameOrEmail,
    password,
    deviceToken: getDeviceToken(),
  });
  if (auth?.twoFactorRequired) {
    return auth;
  }
  store(auth);
  return auth;
}

/* כניסה/הרשמה עם Google — שולח לשרת את ה-credential שהתקבל מכפתור גוגל */
export async function loginWithGoogle(credential) {
  const auth = await api.post("/api/auth/google", {
    credential,
    deviceToken: getDeviceToken(),
  });
  if (auth?.twoFactorRequired) {
    return auth;
  }
  store(auth);
  return auth;
}

/*
  שלב שני בכניסה: הקוד שהתקבל, או קוד גיבוי. רק כאן נשמר הטוקן.
  אסימון המכשיר נשמר **אחרי** store, כי store מנקה את המטמון המקומי כשמתחבר
  משתמש אחר במכשיר הזה.
*/
export async function verifyTwoFactor({ challengeId, code, rememberDevice }) {
  const auth = await api.post("/api/auth/2fa/verify", {
    challengeId,
    code,
    rememberDevice: Boolean(rememberDevice),
  });
  store(auth);
  storeDeviceToken(auth.deviceToken);
  return auth;
}

/* שליחת הקוד שוב — אפשר בערוץ אחר (זה מה שמציל כשאין גישה למייל כרגע). */
export function resendTwoFactorCode({ challengeId, channel }) {
  return api.post("/api/auth/2fa/resend", { challengeId, channel });
}

export function logout() {
  // ניקוי מלא של נתוני החשבון במכשיר — כדי שלא יישאר שריד של החשבון הקודם
  // (מוסדות, צוות, מטמון) למי שיתחבר אחריו. אסימון המכשיר (2FA) ובחירת העוגיות
  // נשמרים כי הם של המכשיר ולא של החשבון.
  clearCachedAppData();
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(DATA_OWNER_KEY);
}

/*
  installAccountSwitchGuard — שומר מפני המצב: התחברות לחשבון א׳ → יציאה →
  התחברות לחשבון ב׳ → "חזור" → חזרה לחשבון א׳.

  הסיבה: היציאה היא ניווט-עמוד מלא (window.location), ולכן הדפדפן שומר את עמוד
  חשבון א׳ ב-bfcache. לחיצה על "חזור" משחזרת אותו מהזיכרון עם נתוני א׳, בזמן
  ש-localStorage כבר שייך לחשבון ב׳ — כלומר מסך של חשבון שכבר לא מחובר.

  הפתרון: כשעמוד משוחזר מה-bfcache (event.persisted) טוענים אותו מחדש, כך
  שהאפליקציה תיבנה מהמצב האמיתי ב-localStorage (החשבון הנוכחי, או מסך הכניסה אם
  התנתקו). טעינה-מחדש אינה יוצרת לולאה: הטעינה החדשה אינה "persisted".
*/
export function installAccountSwitchGuard() {
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      window.location.reload();
    }
  });
}

/*
  ייצוא כל המידע השמור על המשתמשת ועל הגנים שלה ("זכות העיון"). מחזיר
  אובייקט שהמסך הופך לקובץ להורדה. לא כולל סיסמאות או טוקני גישה.
*/
export async function exportMyData() {
  return api.get("/api/account/export");
}

/*
  מחיקת החשבון וכל הנתונים מהשרת ("הזכות להימחק"). לאחר הצלחה מנקים את כל
  המידע המקומי במכשיר (כולל טוקן, פרטי משתמש ומטמון האפליקציה) — כאילו מעולם
  לא נכנסו כאן.
*/
export async function deleteAccount() {
  await api.del("/api/account");
  Object.keys(localStorage)
    .filter((key) => key.startsWith("vaadygo."))
    .forEach((key) => localStorage.removeItem(key));
}

/* שינוי סיסמה למשתמשת המחוברת (משימה: הגדרות → שינוי סיסמה). */
export function changePassword({ currentPassword, newPassword }) {
  return api.post("/api/auth/change-password", { currentPassword, newPassword });
}

/*
  איפוס סיסמה — שלב 1: בקשת קוד חד-פעמי למייל. השרת שולח קוד בן 6 ספרות
  (אם המייל רשום). מטעמי אבטחה השרת לא חושף אם המייל קיים — תמיד מצליח.
*/
export function requestPasswordReset(email) {
  return api.post("/api/auth/forgot-password", { email });
}

/* איפוס סיסמה — שלב 2: הזנת הקוד שהתקבל במייל + סיסמה חדשה. */
export function resetPassword({ email, code, newPassword }) {
  return api.post("/api/auth/reset-password", { email, code, newPassword });
}
